import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { api } from "../lib/api";
import { useApp } from "../context/AppContext";
import { Card, CardContent } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Loader2, Plus, Trash2, Home, Car, Banknote, Bitcoin, Package, Pencil, X } from "lucide-react";
import { toast } from "sonner";
import CountUp from "./CountUp";
import InlineEdit from "./InlineEdit";

const fmt = (n) =>
    new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(n || 0);

const ASSET_TYPES = [
    { key: "real_estate", icon: Home, label_es: "Bienes raíces", label_en: "Real estate" },
    { key: "vehicle", icon: Car, label_es: "Vehículo", label_en: "Vehicle" },
    { key: "cash", icon: Banknote, label_es: "Efectivo", label_en: "Cash" },
    { key: "crypto", icon: Bitcoin, label_es: "Cripto", label_en: "Crypto" },
    { key: "other", icon: Package, label_es: "Otro", label_en: "Other" },
];

const iconFor = (type) => ASSET_TYPES.find((a) => a.key === type)?.icon || Package;
const labelFor = (type, lang) => {
    const t = ASSET_TYPES.find((a) => a.key === type);
    if (!t) return type;
    return lang === "es" ? t.label_es : t.label_en;
};

const ManualAssets = () => {
    const { language } = useApp();
    const [data, setData] = useState({ assets: [], total: 0 });
    const [loading, setLoading] = useState(true);
    const [editing, setEditing] = useState(null); // asset object or null
    const [showForm, setShowForm] = useState(false);
    const [form, setForm] = useState({ asset_type: "real_estate", name: "", value: "", notes: "" });
    const [saving, setSaving] = useState(false);

    const load = async () => {
        try {
            const res = await api.get("/manual-assets");
            setData(res.data);
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => { load(); }, []);

    const openNew = () => {
        setEditing(null);
        setForm({ asset_type: "real_estate", name: "", value: "", notes: "" });
        setShowForm(true);
    };
    const openEdit = (a) => {
        setEditing(a);
        setForm({ asset_type: a.asset_type, name: a.name, value: String(a.value), notes: a.notes || "" });
        setShowForm(true);
    };
    const closeForm = () => { setShowForm(false); setEditing(null); };

    const save = async () => {
        if (!form.name || !form.value) {
            toast.error(language === "es" ? "Nombre y valor son requeridos" : "Name and value required");
            return;
        }
        setSaving(true);
        try {
            const body = {
                asset_type: form.asset_type,
                name: form.name,
                value: parseFloat(form.value),
                notes: form.notes || null,
            };
            if (editing) {
                await api.patch(`/manual-assets/${editing.asset_id}`, body);
            } else {
                await api.post("/manual-assets", body);
            }
            toast.success(language === "es" ? "Guardado" : "Saved");
            closeForm();
            await load();
        } catch (e) {
            toast.error(language === "es" ? "No se pudo guardar" : "Save failed");
        } finally {
            setSaving(false);
        }
    };

    const remove = async (a) => {
        const msg = language === "es" ? `¿Eliminar ${a.name}?` : `Delete ${a.name}?`;
        if (!window.confirm(msg)) return;
        try {
            await api.delete(`/manual-assets/${a.asset_id}`);
            toast.success(language === "es" ? "Eliminado" : "Deleted");
            await load();
        } catch (e) {
            toast.error(language === "es" ? "No se pudo eliminar" : "Delete failed");
        }
    };

    // Inline patch — updates a single field without opening the modal form.
    const patchAsset = async (a, patch) => {
        try {
            await api.patch(`/manual-assets/${a.asset_id}`, {
                asset_type: a.asset_type,
                name: a.name,
                value: a.value,
                notes: a.notes || null,
                ...patch,
            });
            await load();
        } catch (e) {
            toast.error(language === "es" ? "No se pudo actualizar" : "Update failed");
        }
    };

    return (
        <div data-testid="manual-assets-manager">
            <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
                <div>
                    <div className="text-[11px] font-bold tracking-[0.24em] text-[hsl(var(--muted-foreground))] uppercase">
                        {language === "es" ? "ACTIVOS · MANUALES" : "MANUAL · ASSETS"}
                    </div>
                    <div className="text-sm text-[hsl(var(--muted-foreground))]">
                        {language === "es"
                            ? "Añade lo que Caesar no puede ver: casa, auto, efectivo, cripto."
                            : "Add what Caesar can't see: home, car, cash, crypto."}
                    </div>
                </div>
                <Button data-testid="manual-asset-add" onClick={openNew} className="pill gap-2">
                    <Plus className="w-4 h-4" />
                    {language === "es" ? "Añadir activo" : "Add asset"}
                </Button>
            </div>

            {!loading && data.assets.length > 0 && (
                <div className="mb-4 flex items-baseline gap-3">
                    <div className="text-[11px] font-bold tracking-[0.24em] text-[hsl(var(--muted-foreground))] uppercase">
                        {language === "es" ? "Valor total" : "Total value"}
                    </div>
                    <div className="font-display font-extrabold text-2xl tabular-nums">
                        <CountUp value={data.total} format={fmt} />
                    </div>
                </div>
            )}

            <AnimatePresence>
                {showForm && (
                    <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: "auto" }}
                        exit={{ opacity: 0, height: 0 }}
                        transition={{ duration: 0.3 }}
                        className="mb-4 overflow-hidden"
                    >
                        <Card className="rounded-2xl bg-[hsl(var(--secondary))]/50 border-[hsl(var(--border))]">
                            <CardContent className="p-5 grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <Label className="text-xs uppercase tracking-[0.14em] text-[hsl(var(--muted-foreground))]">
                                        {language === "es" ? "Tipo" : "Type"}
                                    </Label>
                                    <Select value={form.asset_type} onValueChange={(v) => setForm({ ...form, asset_type: v })}>
                                        <SelectTrigger data-testid="manual-asset-type" className="mt-1 rounded-xl">
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {ASSET_TYPES.map((a) => (
                                                <SelectItem key={a.key} value={a.key}>
                                                    {language === "es" ? a.label_es : a.label_en}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>
                                <div>
                                    <Label className="text-xs uppercase tracking-[0.14em] text-[hsl(var(--muted-foreground))]">
                                        {language === "es" ? "Nombre" : "Name"}
                                    </Label>
                                    <Input
                                        data-testid="manual-asset-name"
                                        value={form.name}
                                        onChange={(e) => setForm({ ...form, name: e.target.value })}
                                        placeholder={language === "es" ? "Casa en Playa" : "Beach House"}
                                        className="mt-1"
                                    />
                                </div>
                                <div>
                                    <Label className="text-xs uppercase tracking-[0.14em] text-[hsl(var(--muted-foreground))]">
                                        {language === "es" ? "Valor (USD)" : "Value (USD)"}
                                    </Label>
                                    <Input
                                        data-testid="manual-asset-value"
                                        type="number"
                                        value={form.value}
                                        onChange={(e) => setForm({ ...form, value: e.target.value })}
                                        placeholder="0"
                                        className="mt-1"
                                    />
                                </div>
                                <div>
                                    <Label className="text-xs uppercase tracking-[0.14em] text-[hsl(var(--muted-foreground))]">
                                        {language === "es" ? "Notas (opcional)" : "Notes (optional)"}
                                    </Label>
                                    <Input
                                        data-testid="manual-asset-notes"
                                        value={form.notes}
                                        onChange={(e) => setForm({ ...form, notes: e.target.value })}
                                        className="mt-1"
                                    />
                                </div>
                                <div className="md:col-span-2 flex gap-2 justify-end">
                                    <Button variant="outline" className="pill" onClick={closeForm}>
                                        <X className="w-4 h-4 mr-1" />
                                        {language === "es" ? "Cancelar" : "Cancel"}
                                    </Button>
                                    <Button data-testid="manual-asset-save" onClick={save} disabled={saving} className="pill">
                                        {saving && <Loader2 className="w-4 h-4 mr-1 animate-spin" />}
                                        {editing
                                            ? (language === "es" ? "Actualizar" : "Update")
                                            : (language === "es" ? "Guardar" : "Save")}
                                    </Button>
                                </div>
                            </CardContent>
                        </Card>
                    </motion.div>
                )}
            </AnimatePresence>

            {loading ? (
                <div className="flex items-center gap-2 text-sm text-[hsl(var(--muted-foreground))]">
                    <Loader2 className="w-4 h-4 animate-spin" />
                    {language === "es" ? "Cargando..." : "Loading..."}
                </div>
            ) : data.assets.length === 0 ? (
                <div className="text-sm text-[hsl(var(--muted-foreground))] py-4">
                    {language === "es"
                        ? "Aún no has añadido activos manuales."
                        : "You haven't added any manual assets yet."}
                </div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {data.assets.map((a) => {
                        const Icon = iconFor(a.asset_type);
                        return (
                            <motion.div
                                key={a.asset_id}
                                initial={{ opacity: 0, y: 8 }}
                                animate={{ opacity: 1, y: 0 }}
                                data-testid={`manual-asset-item-${a.asset_id}`}
                                className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 p-4 rounded-2xl bg-[hsl(var(--card))] border border-[hsl(var(--border))]"
                            >
                                <div className="flex items-center gap-3 min-w-0">
                                    <div className="w-10 h-10 rounded-xl grid place-items-center bg-[hsl(var(--primary))]/10 text-[hsl(var(--primary))] shrink-0">
                                        <Icon className="w-4 h-4" />
                                    </div>
                                    <div className="min-w-0 flex-1">
                                        <InlineEdit
                                            value={a.name}
                                            onSave={(v) => patchAsset(a, { name: v })}
                                            testId={`manual-asset-name-${a.asset_id}`}
                                            className="font-semibold block truncate"
                                            inputClassName="text-sm font-semibold w-40"
                                        />
                                        <div className="text-xs text-[hsl(var(--muted-foreground))]">{labelFor(a.asset_type, language)}</div>
                                    </div>
                                </div>
                                <div className="flex items-center gap-2 justify-between sm:justify-end pl-13 sm:pl-0">
                                    <InlineEdit
                                        value={a.value}
                                        onSave={(v) => patchAsset(a, { value: v })}
                                        type="number"
                                        min={0}
                                        step={1}
                                        format={fmt}
                                        testId={`manual-asset-value-${a.asset_id}`}
                                        className="font-display font-extrabold text-base sm:text-lg tabular-nums text-emerald-600 dark:text-emerald-400"
                                        inputClassName="text-right text-base sm:text-lg font-display font-extrabold tabular-nums w-28 sm:w-32 text-emerald-600 dark:text-emerald-400"
                                    />
                                    <button
                                        aria-label="edit"
                                        onClick={() => openEdit(a)}
                                        className="w-8 h-8 grid place-items-center rounded-full hover:bg-[hsl(var(--secondary))] transition-colors"
                                    >
                                        <Pencil className="w-3.5 h-3.5" />
                                    </button>
                                    <button
                                        data-testid={`manual-asset-delete-${a.asset_id}`}
                                        aria-label="delete"
                                        onClick={() => remove(a)}
                                        className="w-8 h-8 grid place-items-center rounded-full hover:bg-[hsl(var(--destructive))]/10 text-[hsl(var(--destructive))] transition-colors"
                                    >
                                        <Trash2 className="w-3.5 h-3.5" />
                                    </button>
                                </div>
                            </motion.div>
                        );
                    })}
                </div>
            )}
        </div>
    );
};

export default ManualAssets;
